// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { addToChainAfter, ApiHandlerDiscreteParameter } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    chain as UpdateCartLineChain,
    ExecuteOnSuccessCallback,
} from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { parseProductCategories } from "@sc/XDL/Utilities";

interface UpdateCartLineParameter {
    cartLine: CartLineModel;
}

type HandlerType = ApiHandlerDiscreteParameter<UpdateCartLineParameter, any>;

export const XngGaEditCart: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    if (state.xGoogleAnalytics.editCartEvent) {
        const dataLayer = getDataLayer();
        const session = getSession(state);
        const cart = getCurrentCartState(state);

        if (!dataLayer || !cart.value) {
            return;
        }

        const oldCartLine = cart.value.cartLines?.find(line => line.id === props.parameter.cartLine.id);

        if (!oldCartLine || oldCartLine.qtyOrdered === props.parameter.cartLine.qtyOrdered) {
            return;
        }
        const updatedCartLine = props.parameter.cartLine;
        const quantityChange = (updatedCartLine.qtyOrdered || 0) - (oldCartLine.qtyOrdered || 0);

        const currentPage = getCurrentPage(state);
        const location = currentPage.type === "CartPage" ? "cart" : "mini cart";
        const totalPrice =
            updatedCartLine.pricing?.extendedUnitNetPrice ||
            (updatedCartLine.pricing?.unitNetPrice && updatedCartLine.qtyOrdered
                ? updatedCartLine.pricing.unitNetPrice * updatedCartLine.qtyOrdered
                : 0);

        const googleAnalyticsData = {
            event: state.xGoogleAnalytics.editCartEventName,
            ecommerce: {
                currency: session?.currency?.currencyCode,
                value: totalPrice,
                quantity_change: quantityChange.toString(),
                location,
                affiliation: state.xGoogleAnalytics.affiliation,
                sales_rep: state.xGoogleAnalytics.salesRep || "",
                is_rep: state.xGoogleAnalytics.isRep || "",
                bill_to_num: state.xGoogleAnalytics.billToNum || "",
                ship_to_num: state.xGoogleAnalytics.shipToNum || "",
                cust_type: state.xGoogleAnalytics.customerType || "",
                login_type: state.xGoogleAnalytics.loginType || "",
                warehouse: state.xGoogleAnalytics.warehouse || "",
                items: [
                    {
                        item_id: updatedCartLine.erpNumber,
                        item_name: updatedCartLine.shortDescription,
                        affiliation: state.xGoogleAnalytics.affiliation,
                        discount: updatedCartLine.pricing
                            ? updatedCartLine.pricing.unitNetPrice < updatedCartLine.pricing.unitListPrice
                                ? updatedCartLine.pricing.unitListPrice - updatedCartLine.pricing.unitNetPrice
                                : 0
                            : 0,
                        index: 0,
                        item_brand: state.xGoogleAnalytics.includeProductBrand
                            ? updatedCartLine.brand?.name || ""
                            : undefined,
                        price: updatedCartLine.pricing?.unitNetPrice,
                        quantity: updatedCartLine.qtyOrdered,
                        item_list_id: state.xGoogleAnalytics.includeProductCategory
                            ? updatedCartLine.properties?.["categoriesIds"] || ""
                            : undefined,
                        location_id: session.pickUpWarehouse?.id,
                        ...parseProductCategories(
                            updatedCartLine?.properties?.itemCategoriesShortDescription,
                            true,
                            state.xGoogleAnalytics.includeProductCategory,
                        ),
                    },
                ],
            },
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(googleAnalyticsData);
    }
};

const _GaEditCart = XngGaEditCart as any;
addToChainAfter(UpdateCartLineChain, ExecuteOnSuccessCallback, _GaEditCart);
